<template>
  <div class="sites-page">

    <!-- Page header -->
    <div class="page-header-wrap">
      <div class="page-header">
        <div>
          <h1 class="page-title">Your websites</h1>
          <p class="page-subtitle">
            {{ sites.length > 0 ? `${sites.length} website${sites.length !== 1 ? 's' : ''} growing your business` : 'No websites yet — let\'s build your first one' }}
          </p>
        </div>
        <router-link to="/create" class="bw-btn bw-btn-accent">
          + Build new website
        </router-link>
      </div>

      <!-- Growth summary bar -->
      <div v-if="sites.length > 0" class="summary-bar">
        <div class="stat-pill">
          <span class="stat-dot"></span>
          <span><span class="stat-num">{{ sites.filter(s => s.status === 'active').length }}</span> live site{{ sites.filter(s => s.status === 'active').length !== 1 ? 's' : '' }}</span>
        </div>
        <div class="stat-pill">
          <span class="stat-icon">👥</span>
          <span>{{ sites.length }} total sites</span>
        </div>
        <div v-if="isAdmin && usageSummary" class="stat-pill stat-pill-ai">
          <span class="stat-dot stat-dot-ai"></span>
          <span><span class="stat-num">{{ formatTokens(usageSummary.tenantTotal?.totalTokens || 0) }}</span> AI tokens · 30d</span>
        </div>
        <div v-if="isAdmin && usageSummary" class="stat-pill stat-pill-ai">
          <span class="stat-icon">$</span>
          <span><span class="stat-num">{{ formatCurrency(usageSummary.tenantTotal?.costUsd || 0) }}</span> AI spend · 30d</span>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="page-content">

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading your websites…</p>
      </div>

      <!-- Sites grid -->
      <div v-else-if="sites.length > 0" class="sites-grid">
        <div
          v-for="site in sites"
          :key="site.id"
          class="site-card"
          :class="{ 'site-card-menu-open': openDropdown === site.id }"
        >
          <!-- Preview area -->
          <div class="card-preview">
            <iframe
              v-if="site.preview_url"
              :key="site.id + '_' + (previewNonces[site.id] || 0)"
              :src="site.preview_url"
              class="preview-iframe"
              sandbox="allow-same-origin allow-scripts"
            />

            <!-- Creating with progress -->
            <div v-else-if="site.status === 'creating'" class="card-creating">
              <div class="creating-spinner"></div>
              <div class="creating-label">{{ siteStages[site.id] || 'Starting…' }}</div>
              <div class="creating-progress-wrap">
                <div
                  class="creating-progress-bar"
                  :style="{ width: stageProgress(siteStages[site.id]) + '%' }"
                ></div>
              </div>
              <div class="creating-steps">
                <span
                  v-for="step in CREATION_STEPS"
                  :key="step.key"
                  :title="step.label"
                  class="creating-step"
                  :class="{
                    'step-done': isStepDone(step.key, siteStages[site.id]),
                    'step-active': isStepActive(step.key, siteStages[site.id]),
                  }"
                ></span>
              </div>
            </div>

            <!-- Failed -->
            <div v-else-if="site.status === 'failed'" class="card-failed">
              <div class="failed-icon">⚠</div>
              <p class="failed-label">Creation failed</p>
              <button
                class="retry-btn"
                :disabled="retrying[site.id]"
                @click.stop="retrySite(site.id)"
              >
                {{ retrying[site.id] ? 'Retrying…' : 'Retry' }}
              </button>
            </div>

            <!-- No preview yet -->
            <div v-else class="card-no-preview">
              <div class="no-preview-icon">🌐</div>
            </div>
          </div>

          <!-- Card body -->
          <div class="card-body">
            <div class="card-name">{{ site.name }}</div>
            <div class="card-domain">{{ site.subdomain }}.beyondwebsite.ai</div>

            <div class="card-meta-row">
              <!-- Runtime badge -->
              <span
                v-if="getRuntimeBadge(site.id)"
                class="runtime-badge"
                :class="{
                  'runtime-healthy': getRuntimeBadge(site.id).label === 'Healthy',
                  'runtime-starting': getRuntimeBadge(site.id).label === 'Starting',
                  'runtime-down': getRuntimeBadge(site.id).label === 'Down',
                }"
              >
                {{ getRuntimeBadge(site.id).label }}
              </span>
              <!-- Status badge -->
              <span
                class="status-badge"
                :class="{
                  'status-live': site.status === 'active',
                  'status-creating': site.status === 'creating',
                  'status-failed': site.status === 'failed',
                }"
              >
                <span v-if="site.status === 'active'" class="status-dot-green"></span>
                {{ statusLabel(site.status, siteStages[site.id]) }}
              </span>
            </div>


            <div class="card-footer">
              <span class="card-date">{{ formatDate(site.created_at) }}</span>
              <div class="card-actions" @click.stop>
                <router-link
                  :to="{ name: 'editor', params: { id: site.id } }"
                  class="card-editor-btn"
                >Open Editor →</router-link>
                <a
                  v-if="site.preview_url"
                  :href="site.preview_url"
                  target="_blank"
                  class="card-link-btn"
                >View Live ↗</a>

                <!-- Dropdown -->
                <div class="card-more-wrap" @click.stop>
                  <button class="card-more-btn" @click="toggleDropdown(site.id)">⋯</button>
                  <div v-if="openDropdown === site.id" class="card-dropdown">
                    <button
                      class="dd-item"
                      :disabled="previewRefreshing[site.id]"
                      @click="refreshPreview(site.id); closeDropdown()"
                    >
                      {{ previewRefreshing[site.id] ? 'Refreshing…' : 'Refresh Preview' }}
                    </button>
                    <router-link
                      :to="{ name: 'SiteSettings', params: { id: site.id } }"
                      class="dd-item"
                      @click="closeDropdown()"
                    >Settings</router-link>
                    <div class="dd-sep"></div>
                    <button
                      class="dd-item dd-danger"
                      @click="confirmDelete(site); closeDropdown()"
                    >Delete site</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add new card -->
        <router-link to="/create" class="site-card-new">
          <div class="new-plus">+</div>
          <div class="new-label">Build a new website</div>
        </router-link>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state">
        <div class="empty-icon">✦</div>
        <div class="empty-title">Ready to build your first website?</div>
        <div class="empty-sub">Answer a few questions about your business and we'll have your site live in minutes — no design skills needed.</div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useSitesStore } from '../stores/sites'
import { format } from 'date-fns'
import api from '../utils/api'

// ---------------------------------------------------------------------------
// Creation stage definitions — order matters (used for progress + step pills)
// ---------------------------------------------------------------------------
const CREATION_STEPS = [
  { key: 'template',  label: 'Template',  events: ['template_clone_started', 'template_clone_completed'] },
  { key: 'style',     label: 'Style',     events: ['style_application_started', 'style_application_completed', 'style_eval_started', 'style_eval_completed', 'component_generation_started', 'component_generation_completed'] },
  { key: 'content',   label: 'Content',   events: ['content_generation_started', 'content_generation_completed', 'content_eval_started', 'content_eval_completed', 'repo_import_started', 'repo_import_completed'] },
  { key: 'build',     label: 'Build',     events: ['inspiration_analysis_started', 'inspiration_analysis_completed', 'brand_extraction_started', 'brand_extraction_completed'] },
  { key: 'preview',   label: 'Preview',   events: ['creation_completed'] },
]

const STAGE_MAP = {
  creation_started:               { label: 'Starting...',                   pct: 3  },
  template_clone_started:         { label: 'Cloning template...',           pct: 8  },
  template_clone_completed:       { label: 'Template ready',                pct: 15 },
  style_application_started:      { label: 'Applying inspiration style...', pct: 22 },
  style_application_completed:    { label: 'Style applied',                 pct: 35 },
  style_eval_started:             { label: 'Evaluating style match...',     pct: 40 },
  style_eval_completed:           { label: 'Style evaluated',               pct: 45 },
  content_generation_started:     { label: 'Generating brand content...',   pct: 52 },
  content_generation_completed:   { label: 'Content generated',             pct: 65 },
  content_eval_started:           { label: 'Evaluating content...',         pct: 68 },
  content_eval_completed:         { label: 'Content evaluated',             pct: 72 },
  component_generation_started:   { label: 'Analysing & generating...',     pct: 30 },
  component_generation_completed: { label: 'Components generated',          pct: 60 },
  repo_import_started:            { label: 'Importing repository...',       pct: 30 },
  repo_import_completed:          { label: 'Repository imported',           pct: 60 },
  inspiration_analysis_started:   { label: 'Analysing inspiration...',      pct: 75 },
  inspiration_analysis_completed: { label: 'Analysis complete',             pct: 80 },
  brand_extraction_started:       { label: 'Extracting brand data...',      pct: 75 },
  brand_extraction_completed:     { label: 'Brand data ready',              pct: 80 },
  creation_completed:             { label: 'Complete!',                     pct: 100 },
  creation_failed:                { label: 'Creation failed',               pct: 100 },
}

const ALL_STAGE_EVENTS = Object.keys(STAGE_MAP)

// Which step index does an event belong to?
const eventToStepIndex = (eventType) => {
  for (let i = 0; i < CREATION_STEPS.length; i++) {
    if (CREATION_STEPS[i].events.includes(eventType)) return i
  }
  return -1
}

export default {
  name: 'Sites',
  components: {},
  setup() {
    const siteStore = useSitesStore()
    const sites     = computed(() => siteStore.sites)
    const isAdmin   = computed(() => Boolean(siteStore.user?.isAdmin || siteStore.user?.is_admin))
    const loading   = ref(true)
    const previewRefreshing = ref({})
    const previewNonces     = ref({})
    const openDropdown      = ref(null)
    const runtimeBySite     = ref({})
    const runtimePoller     = ref(null)
    const usageSummary      = ref(null)
    const usageLoading      = ref(false)

    // Per-site stage tracking
    const siteStages    = ref({})   // { [siteId]: label string }
    const siteEventKeys = ref({})   // { [siteId]: last eventType }
    const sseConns      = {}        // plain object, not reactive (EventSource refs)

    // -----------------------------------------------------------------------
    // SSE per-site connection
    // -----------------------------------------------------------------------
    const connectSiteSSE = (siteId) => {
      if (sseConns[siteId]) return

      const token = localStorage.getItem('bwai_token')
      if (!token) return

      const es = new EventSource(
        `/api/bwai/sites/${siteId}/stream?token=${encodeURIComponent(token)}`
      )
      sseConns[siteId] = es

      const handleEvent = (event) => {
        const entry = STAGE_MAP[event.type]
        if (entry) {
          siteStages.value    = { ...siteStages.value,    [siteId]: entry.label }
          siteEventKeys.value = { ...siteEventKeys.value, [siteId]: event.type  }
        }
        if (event.type === 'creation_completed' || event.type === 'creation_failed') {
          disconnectSiteSSE(siteId)
          loadSites()
        }
      }

      // Force-reload iframe when Nuxt rebuilds after a content update
      const handlePreviewReady = () => {
        previewNonces.value = { ...previewNonces.value, [siteId]: Date.now() }
      }

      ALL_STAGE_EVENTS.forEach(type => es.addEventListener(type, handleEvent))
      es.addEventListener('preview_ready', handlePreviewReady)
      es.onerror = () => { /* EventSource auto-reconnects */ }
    }

    const disconnectSiteSSE = (siteId) => {
      if (sseConns[siteId]) {
        sseConns[siteId].close()
        delete sseConns[siteId]
      }
    }

    const disconnectAllSSE = () => {
      Object.keys(sseConns).forEach(disconnectSiteSSE)
    }

    // -----------------------------------------------------------------------
    // Helpers for template
    // -----------------------------------------------------------------------
    const stageProgress = (label) => {
      const entry = Object.values(STAGE_MAP).find(e => e.label === label)
      return entry ? entry.pct : 3
    }

    const isStepDone = (stepKey, currentLabel) => {
      const currentEntry = Object.entries(STAGE_MAP).find(([, v]) => v.label === currentLabel)
      if (!currentEntry) return false
      const currentPct   = currentEntry[1].pct
      const stepMaxPct   = Math.max(...CREATION_STEPS.find(s => s.key === stepKey).events
        .map(e => STAGE_MAP[e]?.pct ?? 0))
      return currentPct > stepMaxPct
    }

    const isStepActive = (stepKey, currentLabel) => {
      const step = CREATION_STEPS.find(s => s.key === stepKey)
      if (!step) return false
      return step.events.some(e => STAGE_MAP[e]?.label === currentLabel)
    }

    const statusLabel = (status, stageLabel) => {
      if (status === 'creating' && stageLabel) return stageLabel
      return status
    }

    const getStatusClass = (status) => ({
      'bg-green-100 text-green-800':  status === 'active',
      'bg-violet-100 text-violet-800':status === 'creating',
      'bg-red-100 text-red-800':      status === 'failed',
      'bg-gray-100 text-gray-800':    status === 'archived',
    })

    const getRuntimeBadge = (siteId) => {
      const runtime = runtimeBySite.value[siteId]
      if (!runtime) return null
      if (runtime.reachable) {
        return { label: 'Healthy', className: 'bg-emerald-100 text-emerald-700' }
      }
      if (runtime.status === 'running') {
        return { label: 'Starting', className: 'bg-amber-100 text-amber-700' }
      }
      return { label: 'Down', className: 'bg-rose-100 text-rose-700' }
    }

    const formatDate = (dateString) =>
      format(new Date(dateString), 'MMM d, yyyy')

    const formatTokens = (value) => {
      const n = Number(value || 0)
      if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`
      if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`
      return String(Math.round(n))
    }

    const formatCurrency = (value) => {
      const n = Number(value || 0)
      if (n <= 0) return '$0'
      if (n < 0.01) return '<$0.01'
      return `$${n.toFixed(n >= 10 ? 0 : 2)}`
    }

    const emptyUsage = Object.freeze({
      inputTokens: 0,
      outputTokens: 0,
      totalTokens: 0,
      costUsd: 0,
      tokens24h: 0,
      cost24h: 0,
      lastUsedAt: null,
    })

    const getSiteUsage = (siteId) => usageSummary.value?.bySite?.[siteId] || emptyUsage

    // -----------------------------------------------------------------------
    // Data loading
    // -----------------------------------------------------------------------
    const loadSites = async () => {
      loading.value = true
      try {
        await siteStore.fetchSites()
        await loadRuntimeStatuses()
        if (isAdmin.value) await loadUsageSummary()
        // Open SSE for creating sites (stage progress) and active sites (preview_ready)
        sites.value
          .filter(s => s.status === 'creating' || s.status === 'active')
          .forEach(s => connectSiteSSE(s.id))
      } catch (error) {
        console.error('Failed to load sites:', error)
      } finally {
        loading.value = false
      }
    }

    const loadRuntimeStatuses = async () => {
      try {
        const response = await api.get('/sites/preview/runtimes')
        const next = {}
        const items = response.data?.items || []
        items.forEach((item) => { next[item.siteId] = item })
        runtimeBySite.value = next
      } catch (error) {
        console.error('Failed to load preview runtime statuses:', error)
      }
    }

    const loadUsageSummary = async () => {
      usageLoading.value = true
      try {
        const response = await api.get('/sites/usage/summary', { params: { days: 30 } })
        usageSummary.value = response.data?.usage || null
      } catch (error) {
        usageSummary.value = null
        console.error('Failed to load AI usage summary:', error)
      } finally {
        usageLoading.value = false
      }
    }

    const refreshPreview = async (siteId) => {
      previewRefreshing.value = { ...previewRefreshing.value, [siteId]: true }
      try {
        await siteStore.refreshSitePreview(siteId)
        previewNonces.value = { ...previewNonces.value, [siteId]: Date.now() }
      } catch (error) {
        console.error('Failed to refresh preview:', error)
      } finally {
        previewRefreshing.value = { ...previewRefreshing.value, [siteId]: false }
      }
    }

    const retrying = ref({})
    const retrySite = async (siteId) => {
      retrying.value = { ...retrying.value, [siteId]: true }
      try {
        await api.post(`/sites/${siteId}/retry`)
        // Optimistically flip the site to creating so the progress UI appears
        const site = sites.value.find(s => s.id === siteId)
        if (site) site.status = 'creating'
        siteStages.value = { ...siteStages.value, [siteId]: 'Starting…' }
        connectSiteSSE(siteId)
      } catch (error) {
        console.error('Failed to retry site:', error)
        alert('Could not retry — ' + (error.response?.data?.error || error.message))
      } finally {
        retrying.value = { ...retrying.value, [siteId]: false }
      }
    }

    const toggleDropdown  = (siteId) => { openDropdown.value = openDropdown.value === siteId ? null : siteId }
    const closeDropdown   = ()         => { openDropdown.value = null }
    const confirmDelete   = (site)     => {
      if (confirm(`Are you sure you want to delete "${site.name}"? This action cannot be undone.`)) {
        deleteSite(site.id)
      }
    }
    const deleteSite = async (siteId) => {
      try {
        await siteStore.deleteSite(siteId)
      } catch (error) {
        console.error('Failed to delete site:', error)
        alert('Failed to delete site. Please try again.')
      }
    }

    onMounted(() => {
      loadSites()
      runtimePoller.value = setInterval(loadRuntimeStatuses, 20000)
      document.addEventListener('click', closeDropdown)
    })

    onUnmounted(() => {
      disconnectAllSSE()
      if (runtimePoller.value) clearInterval(runtimePoller.value)
      document.removeEventListener('click', closeDropdown)
    })

    return {
      sites,
      loading,
      siteStages,
      CREATION_STEPS,
      stageProgress,
      isStepDone,
      isStepActive,
      statusLabel,
      getStatusClass,
      getRuntimeBadge,
      isAdmin,
      usageSummary,
      usageLoading,
      getSiteUsage,
      formatTokens,
      formatCurrency,
      formatDate,
      previewRefreshing,
      previewNonces,
      refreshPreview,
      retrying,
      retrySite,
      openDropdown,
      toggleDropdown,
      closeDropdown,
      confirmDelete,
      deleteSite,
    }
  }
}
</script>

<style scoped>
.sites-page { min-height: 100vh; }

/* ── Page header ── */
.page-header-wrap {
  padding: 32px 32px 0;
  max-width: 1200px;
  margin: 0 auto;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 24px;
}
.page-title {
  font-size: 28px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.1;
}
.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 6px;
}

/* ── Summary bar ── */
.summary-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}
.stat-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 100px;
  background: var(--glass-bg);
  border: var(--glass-border);
  backdrop-filter: var(--glass-blur);
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}
.stat-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--success);
  box-shadow: 0 0 6px var(--success);
  flex-shrink: 0;
}
.stat-dot-ai {
  background: var(--accent);
  box-shadow: 0 0 8px var(--accent-glow);
}
.stat-icon { font-size: 16px; }
.stat-num { color: var(--accent); }
.stat-pill-ai {
  background: linear-gradient(135deg, rgba(124,109,250,0.14), rgba(167,139,250,0.08));
  border-color: rgba(124,109,250,0.24);
}

/* ── Buttons ── */
.bw-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: var(--radius-sm);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
  white-space: nowrap;
  text-decoration: none;
  font-family: inherit;
}
.bw-btn-accent {
  background: var(--accent);
  color: #fff;
  box-shadow: 0 0 18px var(--accent-glow);
}
.bw-btn-accent:hover {
  background: #6d5ef0;
  box-shadow: 0 0 28px var(--accent-glow);
  transform: translateY(-1px);
  color: #fff;
}
.bw-btn-lg {
  padding: 13px 28px;
  font-size: 16px;
  font-weight: 700;
  border-radius: var(--radius-md);
}

/* ── Page content ── */
.page-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px 80px;
}

/* ── Loading ── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  gap: 16px;
}
.loading-spinner {
  width: 36px;
  height: 36px;
  border: 2.5px solid rgba(124,109,250,0.25);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-text { font-size: 14px; color: var(--text-secondary); }

/* ── Sites grid ── */
.sites-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}
@media (max-width: 1024px) { .sites-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px)  { .sites-grid { grid-template-columns: 1fr; } }

/* ── Site card ── */
.site-card {
  position: relative;
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: var(--glass-border);
  border-radius: var(--radius-lg);
  overflow: visible;
  transition: all 0.2s ease;
  box-shadow: var(--elev-sm);
}
.site-card-menu-open {
  z-index: 20;
}
.site-card:hover {
  background: var(--glass-bg-hover);
  border: var(--glass-border-hi);
  box-shadow: var(--elev-md);
  transform: translateY(-2px);
}

/* Preview */
.card-preview {
  height: 160px;
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  background: rgba(8,8,16,0.6);
  border-top-left-radius: var(--radius-lg);
  border-top-right-radius: var(--radius-lg);
}
.preview-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  pointer-events: none;
}

/* Creating state */
.card-creating {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(124,109,250,0.2), rgba(167,139,250,0.12));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.creating-spinner {
  width: 28px;
  height: 28px;
  border: 2px solid rgba(124,109,250,0.25);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
.creating-label {
  font-size: 11px;
  font-weight: 600;
  color: rgba(167,139,250,0.9);
  letter-spacing: 0.03em;
  text-align: center;
  padding: 0 12px;
}
.creating-progress-wrap {
  width: 80%;
  height: 3px;
  background: rgba(255,255,255,0.1);
  border-radius: 2px;
  overflow: hidden;
}
.creating-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, var(--accent), var(--accent-light));
  border-radius: 2px;
  transition: width 0.7s ease;
}
.creating-steps {
  display: flex;
  align-items: center;
  gap: 4px;
}
.creating-step {
  height: 4px;
  width: 8px;
  border-radius: 2px;
  background: rgba(255,255,255,0.15);
  transition: all 0.4s;
}
.creating-step.step-done  { width: 14px; background: var(--accent); }
.creating-step.step-active { width: 14px; background: rgba(167,139,250,0.7); animation: pulse-step 1.5s ease-in-out infinite; }
@keyframes pulse-step { 0%,100%{opacity:.7} 50%{opacity:1} }

/* Failed state */
.card-failed {
  width: 100%;
  height: 100%;
  background: rgba(248,113,113,0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.failed-icon { font-size: 28px; color: var(--danger); }
.failed-label { font-size: 12px; color: var(--danger); font-weight: 500; }
.retry-btn {
  margin-top: 4px;
  padding: 6px 18px;
  border-radius: 999px;
  border: 1px solid var(--danger);
  background: transparent;
  color: var(--danger);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  font-family: inherit;
}
.retry-btn:hover:not(:disabled) { background: var(--danger); color: #fff; }
.retry-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* No preview */
.card-no-preview {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.no-preview-icon { font-size: 32px; opacity: 0.25; }

/* ── Card body ── */
.card-body { padding: 14px 16px; }
.card-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-domain {
  font-size: 11px;
  color: var(--text-muted);
  font-family: monospace;
  margin-bottom: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Meta row */
.card-meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}
.status-live    { background: rgba(52,211,153,0.12); border: 1px solid rgba(52,211,153,0.25); color: var(--success); }
.status-creating{ background: rgba(124,109,250,0.12); border: 1px solid rgba(124,109,250,0.25); color: var(--accent-light); }
.status-failed  { background: rgba(248,113,113,0.12); border: 1px solid rgba(248,113,113,0.25); color: var(--danger); }
.status-dot-green {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--success); box-shadow: 0 0 5px var(--success);
}

.runtime-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 9999px;
  font-size: 11px; font-weight: 600;
}
.runtime-healthy { background: rgba(52,211,153,0.12); color: var(--success); border: 1px solid rgba(52,211,153,0.2); }
.runtime-starting{ background: rgba(251,191,36,0.12); color: var(--warning); border: 1px solid rgba(251,191,36,0.2); }
.runtime-down   { background: rgba(248,113,113,0.12); color: var(--danger);  border: 1px solid rgba(248,113,113,0.2); }

/* Admin AI usage */
.ai-usage-strip {
  margin: 10px 0;
  padding: 10px 11px;
  border-radius: var(--radius-md);
  background:
    linear-gradient(135deg, rgba(124,109,250,0.12), rgba(167,139,250,0.06)),
    rgba(255,255,255,0.025);
  border: 1px solid rgba(124,109,250,0.18);
}
.ai-usage-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
}
.ai-usage-label {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(167,139,250,0.85);
  font-weight: 800;
}
.ai-usage-window {
  font-size: 11px;
  color: var(--text-muted);
  font-weight: 700;
}
.ai-usage-loading {
  font-size: 11px;
  color: var(--text-muted);
}
.ai-usage-metrics {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 8px;
  align-items: center;
  font-size: 11px;
  color: var(--text-secondary);
}
.ai-usage-metrics span:first-child {
  color: var(--text-primary);
  font-weight: 700;
}

/* Card footer */
.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: var(--glass-border);
}
.card-date { font-size: 11px; color: var(--text-muted); }
.card-actions { display: flex; align-items: center; gap: 8px; }

.card-link-btn {
  font-size: 12px;
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.15s;
}
.card-link-btn:hover { color: var(--success); }

.card-editor-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 11px;
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: var(--accent-soft);
  color: var(--accent);
  border: 1px solid rgba(124,109,250,0.25);
  transition: all 0.15s;
  text-decoration: none;
}
.card-editor-btn:hover {
  background: rgba(124,109,250,0.2);
  border-color: rgba(124,109,250,0.45);
  color: var(--accent);
}

/* Dropdown */
.card-more-wrap { position: relative; }
.card-more-btn {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  border-radius: var(--radius-sm);
  background: transparent; border: none;
  cursor: pointer; color: var(--text-muted);
  font-size: 16px; transition: all 0.15s;
}
.card-more-btn:hover { background: var(--glass-bg-hover); color: var(--text-primary); }

.card-dropdown {
  position: absolute; right: 0; bottom: calc(100% + 4px);
  min-width: 160px; z-index: 200;
  background: rgba(22,22,38,0.97);
  backdrop-filter: blur(20px);
  border: var(--glass-border-hi);
  border-radius: var(--radius-md);
  box-shadow: var(--elev-md);
  padding: 4px;
  animation: fadeDown 0.15s ease;
}
@keyframes fadeDown {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.dd-item {
  display: block; width: 100%;
  padding: 8px 12px; border-radius: var(--radius-sm);
  font-size: 12px; font-weight: 500; cursor: pointer;
  color: var(--text-secondary); transition: all 0.12s;
  background: transparent; border: none; text-align: left;
  text-decoration: none; font-family: inherit;
}
.dd-item:hover { background: var(--glass-bg-hover); color: var(--text-primary); }
.dd-danger { color: var(--danger); }
.dd-danger:hover { background: rgba(248,113,113,0.1); color: var(--danger); }
.dd-sep { height: 1px; background: rgba(255,255,255,0.07); margin: 3px 0; }

/* ── New site card ── */
.site-card-new {
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  border: 1px dashed rgba(255,255,255,0.18);
  border-radius: var(--radius-lg);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 12px; min-height: 260px;
  cursor: pointer; transition: all 0.2s;
  color: var(--text-muted);
  text-decoration: none;
}
.site-card-new:hover {
  background: var(--glass-bg-hover);
  border-color: rgba(124,109,250,0.45);
  color: var(--accent);
  box-shadow: 0 0 24px rgba(124,109,250,0.12);
  transform: translateY(-2px);
}
.new-plus {
  width: 48px; height: 48px; border-radius: 50%;
  background: var(--glass-bg-active);
  border: var(--glass-border);
  display: flex; align-items: center; justify-content: center;
  font-size: 24px; font-weight: 300;
  transition: all 0.2s;
}
.site-card-new:hover .new-plus {
  background: var(--accent-soft);
  border-color: rgba(124,109,250,0.35);
  box-shadow: 0 0 18px rgba(124,109,250,0.2);
}
.new-label { font-size: 14px; font-weight: 600; }

/* ── Empty state ── */
.empty-state {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  text-align: center; padding: 80px 24px; gap: 16px;
}
.empty-icon {
  width: 72px; height: 72px; border-radius: 20px;
  background: var(--accent-soft);
  border: 1px solid rgba(124,109,250,0.3);
  display: flex; align-items: center; justify-content: center;
  font-size: 32px; color: var(--accent);
  animation: pulse-border 2.5s ease-in-out infinite;
  box-shadow: 0 0 40px rgba(124,109,250,0.15);
}
@keyframes pulse-border {
  0%, 100% { box-shadow: 0 0 0 0 var(--accent-glow); }
  50%       { box-shadow: 0 0 0 8px transparent; }
}
.empty-title { font-size: 22px; font-weight: 800; color: var(--text-primary); }
.empty-sub   { font-size: 14px; color: var(--text-secondary); max-width: 440px; line-height: 1.6; }

@media (max-width: 768px) {
  .page-header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .page-header-wrap { padding: 20px 16px 0; }
  .page-content { padding: 16px; }
  .summary-bar { flex-wrap: wrap; gap: 8px; }
}
@media (max-width: 480px) {
  .page-title { font-size: 22px; }
}
</style>
